// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Real Zoho Creator bridge transport.
 *
 * Implements the widget's BridgeTransport interface (bridge/types.ts) against a
 * live db_service_request_bridge + the ProcessServiceRequest resolver seam (#15),
 * over the Gateway postMessage channel (gateway-client.ts) and the shared-record
 * rendezvous (rendezvous.ts). This is the ONE swap point: the orchestrator
 * (ADR-007), BridgeClient, and every feature above depend only on
 * BridgeTransport, so replacing the mock with this is a one-line change in
 * main.tsx.
 *
 * Boundaries kept:
 *   - ADR-002 P6: holds no privileged access; only writes a reference + polls.
 *   - ADR-002 P4/P5: bounded poll; branch on the record's terminal state; a
 *     failure becomes a typed Error record, never an inferred-from-shape success.
 *   - ADR-007 D3: the correlation_id is supplied by the caller (BridgeClient) and
 *     passed straight through onto the stage record.
 *   - ISO/IEC 5055: no silent fallback; a transport/bridge failure maps to an
 *     Error BridgeRecord carrying first_error, so the orchestrator's existing
 *     P5 branch reports it via the uniform halt contract (ADR-006 D4).
 */

import type {
  BridgeRecord,
  BridgeRequestType,
  BridgeSubmitInput,
  BridgeTransport,
} from "./types";
import type { GatewayClient } from "./gateway-client";
import type { PollParams } from "./rendezvous";
import { runExchange } from "./rendezvous";
import { mapDeviceRegistryResponse } from "./device-registry-mapper";
import { mapUpdaterAuResponse } from "./updater-au-response-mapper";
import { mapAu0001AttributionResponse } from "./au0001-response-mapper";
import { mapFirmwareDeliveryMetaResponse } from "./firmware-delivery-meta-response-mapper";
import { mapFirmwareDeliveryOfflineBundlesResponse } from "./firmware-delivery-offline-bundles-mapper";

/**
 * Per-request-kind field-name alignment: some request kinds relay a raw
 * capability response_payload straight through (Zoho / foreign-AU field
 * names), which the real transport reconciles onto the widget's contract
 * shape here before the caller sees it. Kinds absent from this map pass
 * their response_payload through unchanged.
 */
const RESPONSE_MAPPERS: Partial<Record<BridgeRequestType, (raw: unknown) => unknown>> = {
  GetDeviceBySerialNumber: mapDeviceRegistryResponse,
  GetDeviceBySoftwareId: mapDeviceRegistryResponse,
  GetAU0004LatestAccountability: (raw) =>
    mapUpdaterAuResponse(raw, "Update Firmware over BLE"),
  GetAU0008LatestAccountability: (raw) =>
    mapUpdaterAuResponse(raw, "Update Firmware Over Serial"),
  GetAU0001LatestAttributionByDeviceAnchor: mapAu0001AttributionResponse,
  GetFirmwarePackageMetaByReferenceId: mapFirmwareDeliveryMetaResponse,
  ListFirmwarePackageMeta: mapFirmwareDeliveryOfflineBundlesResponse,
};

/** Construction options for the real transport. */
export interface ZohoTransportOptions {
  /** The Gateway postMessage client (constructed at the composition root). */
  readonly client: GatewayClient;
  /** Default bounded-poll parameters (ADR-002 P4). Per-call overrides win. */
  readonly poll: PollParams;
}

/**
 * Build the real BridgeTransport. Each submit() runs one rendezvous exchange for
 * one stage record (ADR-007 D2) under the caller-supplied correlation_id
 * (ADR-007 D3), and resolves a typed BridgeRecord.
 *
 * NOTE (ADR-002 P4 give-up): the interface's optional pollIntervalMs/timeoutMs
 * are honoured as per-call overrides of the default PollParams; an AbortSignal
 * is mapped to an immediate timeout-shaped Error record so the orchestrator's
 * existing bridge_timeout halt path is preserved.
 */
export function createZohoTransport(options: ZohoTransportOptions): BridgeTransport {
  const { client, poll } = options;

  return {
    async submit<TRequest, TResponse>(
      input: BridgeSubmitInput<TRequest>,
    ): Promise<BridgeRecord<TResponse>> {
      const base = {
        correlationId: input.correlationId,
        requestType: input.requestType,
      } as const;

      // Already-aborted fast path (ADR-002 P4 bounded give-up).
      if (input.signal?.aborted === true) {
        return {
          ...base,
          status: "Error",
          firstError: "bridge_timeout",
        } as BridgeRecord<TResponse>;
      }

      // The typed request crosses as a reference in request_payload (ADR-002 P6).
      // TODO(#13): request_payload field link-names are provisional; the resolver
      // reads e.g. `identifier` / `device_anchor` from this content.
      const requestPayload = JSON.stringify(input.request ?? {});

      // Per-call poll overrides (ADR-002 P4): the interface exposes
      // pollIntervalMs/timeoutMs; derive maxAttempts from timeout/interval.
      const effectivePoll: PollParams = derivePoll(poll, input);

      const outcome = await runExchange(
        client,
        input.requestType,
        input.correlationId,
        requestPayload,
        effectivePoll,
      );

      if (outcome.ok) {
        // Some kinds carry a raw foreign-capability record on response_payload;
        // align it onto the widget's contract shape here, the one place the
        // real transport meets those raw field spellings.
        const mapper = RESPONSE_MAPPERS[input.requestType];
        const response = mapper ? mapper(outcome.responsePayload) : outcome.responsePayload;
        return {
          ...base,
          status: "Success",
          response: response as TResponse,
        } as BridgeRecord<TResponse>;
      }

      // Failure -> typed Error record. The orchestrator branches on status and
      // maps firstError onto the uniform halt contract (ADR-006 D4). A timeout
      // kind is surfaced as bridge_timeout so the existing give-up path matches.
      const firstError =
        outcome.error.kind === "timeout"
          ? "bridge_timeout"
          : outcome.error.detail !== undefined
            ? `${outcome.error.kind}: ${outcome.error.detail}`
            : outcome.error.kind;

      return {
        ...base,
        status: "Error",
        firstError,
      } as BridgeRecord<TResponse>;
    },
  };
}

/**
 * Derive effective poll params from the transport default and any per-call
 * overrides on the submit input. Bounded and explicit (ISO/IEC 5055): a supplied
 * timeout/interval sets maxAttempts = ceil(timeout / interval), at least 1.
 */
function derivePoll<TRequest>(
  base: PollParams,
  input: BridgeSubmitInput<TRequest>,
): PollParams {
  const intervalMs = input.pollIntervalMs ?? base.intervalMs;
  if (input.timeoutMs === undefined) {
    return { intervalMs, maxAttempts: base.maxAttempts, sleep: base.sleep };
  }
  const maxAttempts = Math.max(1, Math.ceil(input.timeoutMs / Math.max(1, intervalMs)));
  return { intervalMs, maxAttempts, sleep: base.sleep };
}
